import React, {
  useMemo, useCallback, useRef, useEffect,
} from 'react';
import {
  SectionList,
  Spinner,
  Box,
  HStack,
  Icon,
  Pressable,
  Text,
} from '@gluestack-ui/themed';
import { Settings } from 'lucide-react-native';
import { useTranslation } from 'react-i18next';

import { useDialog } from '@/common/context/DialogContext';
import {
  useRoute, RouteProp, useNavigation,
} from '@react-navigation/native';
import type { NavigationProp } from '@react-navigation/native';
import type { LecturerRouteParamList } from '@/common/navigation/RoleNavigations/Lecturer/types';
import { useLecturerModuleData } from '@/common/hooks/lecturer/useLecturerModuleData';
import { useApiErrorReporter } from '@/common/utils/apiErrorReporter';
import log from '@/common/utils/logger';
import type { Task } from '@/common/api/models/task';

import TaskItem from './TaskItem';
import AddTaskButton from './AddTaskButton';
import LevelHeader from './LevelHeader';

interface TaskListProps {
  visible: boolean;
  isLoading?: boolean;
  /**
   * Optional guard called before the active task is changed. Receives a
   * `proceed` callback that, when invoked, performs the actual selection.
   * Used by ModuleEditor to prompt for unsaved changes.
   */
  onBeforeSelect?: (proceed: () => void) => void;
}

interface Section {
  title: string;
  levelNr: number;
  data: Task[];
}

/**
 * TaskList - shows module settings and content (levels and tasks).
 */
const TaskList: React.FC<TaskListProps> = ({ visible, isLoading: isLoadingProp, onBeforeSelect }) => {
  const { t } = useTranslation();
  const { showDialog, showToastMessage } = useDialog();
  const { reportApiError } = useApiErrorReporter();

  const route = useRoute<RouteProp<LecturerRouteParamList, 'ModuleEditor'>>();
  const moduleId = route.params?.moduleId;
  const version = route.params?.version ?? '0.0';
  const selectedTaskId = route.params?.taskId ?? null;
  const navigation = useNavigation<NavigationProp<LecturerRouteParamList, 'ModuleEditor'>>();
  const selectedTaskIdRef = useRef<string | null | undefined>(selectedTaskId);

  useEffect(() => {
    selectedTaskIdRef.current = selectedTaskId;
  }, [selectedTaskId]);

  const {
    module,
    tasks,
    isLoading,
    createTaskMutation,
    updateTaskMutation,
    deleteTaskMutation,
  } = useLecturerModuleData(moduleId, version, selectedTaskId);

  const isDraft = module?.state?.workflowState === 'draft';
  const isMutating = createTaskMutation.isPending || updateTaskMutation.isPending || deleteTaskMutation.isPending;
  const isReadOnly = !isDraft;

  const isSettingsSelected = selectedTaskId === null;

  const sections = useMemo(() => {
    const safeTasks = (Array.isArray(tasks) ? tasks : []) as Task[];

    const result = (module?.metadata?.levels || []).map((level: { id: number; title: string }) => ({
      title: level.title,
      levelNr: level.id,
      data: safeTasks
        .filter((task) => task.level === level.id)
        .sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0)),
    }));
    return result as Section[];
  }, [module?.metadata?.levels, tasks]);

  const handleAddTask = useCallback(async (level: number) => {
    if (!tasks || isMutating) return;

    const tempId = `temp-${Date.now()}`;
    selectedTaskIdRef.current = tempId;
    navigation.setParams({ taskId: tempId });

    try {
      const highestSortIndex = (tasks as Task[])
        .filter((t) => t.level === level)
        .reduce((max, task) => Math.max(max, task.sortIndex || 0), 0);

      await createTaskMutation.mutateAsync(
        {
          moduleId,
          version,
          tempId,
          data: {
            title: 'New Task',
            level,
            sortIndex: highestSortIndex + 1,
          },
        } as any, // eslint-disable-line @typescript-eslint/no-explicit-any
        {
          onSuccess: (response: { data?: { data?: Task } }) => {
            const newTask = response?.data?.data;
            if (newTask?.id) {
              // Swap tempId for real ID if we are still on this task
              const currentTaskId = selectedTaskIdRef.current;
              if (currentTaskId === tempId) {
                selectedTaskIdRef.current = newTask.id;
                navigation.setParams({ taskId: newTask.id });
              }
            } else {
              log.info('Error creating task:', response);
            }
          },
          onError: (error: Error) => {
            // Revert navigation if we failed and are still on the temp task
            const currentTaskId = selectedTaskIdRef.current;
            if (currentTaskId === tempId) {
              selectedTaskIdRef.current = null;
              navigation.setParams({ taskId: null });
            }
            reportApiError(error, t('lecturer.moduleEditor.taskEditor.buttons.create'));
            log.info('Error creating task:', error);
          },
        },
      );
    } catch (error) {
      log.info('error', error);
    }
  }, [tasks, isMutating, createTaskMutation, moduleId, version, navigation, reportApiError, t]);

  const handleDelete = useCallback(async (task: Task) => {
    if (isMutating) return;
    showDialog({
      title: t('lecturer.moduleEditor.taskEditor.dialogs.deleteTask.title'),
      message: t('lecturer.moduleEditor.taskEditor.dialogs.deleteTask.message'),
      buttons: [
        { text: t('common.buttons.cancel'), variant: 'outline', action: 'secondary' },
        {
          text: t('lecturer.moduleEditor.taskEditor.buttons.delete'),
          action: 'negative',
          onPress: async () => {
            selectedTaskIdRef.current = null;
            navigation.setParams({ taskId: null });
            await deleteTaskMutation.mutateAsync(
              {
                moduleId,
                taskId: task.id,
                version,
              },
              {
                onSuccess: () => {
                  showToastMessage('success', t('lecturer.moduleEditor.taskEditor.messages.deleteSuccess'));
                },
                onError: (error: Error) => {
                  reportApiError(error, t('lecturer.moduleEditor.taskEditor.buttons.delete'));
                },
              },
            );
          },
        },
      ],
    });
  }, [isMutating, showDialog, t, deleteTaskMutation, moduleId, version, showToastMessage, reportApiError, navigation]);

  const handleMove = useCallback(async (task: Task, direction: number) => {
    if (isMutating) return;
    const section = sections.find((s) => s.levelNr === task.level);
    if (!section) return;
    const index = section.data.indexOf(task);
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= section.data.length) return;

    try {
      await updateTaskMutation.mutateAsync(
        {
          moduleId,
          taskId: task.id,
          version,
          data: {
            sortIndex: (task.sortIndex || 0) + direction,
          },
        },
        {
          onError: (error: Error) => {
            reportApiError(error, t('lecturer.moduleEditor.taskEditor.buttons.move'));
            log.info('Error moving task:', error);
          },
        },
      );
    } catch (error) {
      log.info('error', error);
    }
  }, [isMutating, sections, updateTaskMutation, moduleId, version, reportApiError, t]);

  const handleSelect = useCallback((task: Task | null) => {
    const performSelect = () => {
      selectedTaskIdRef.current = task?.id ?? null;
      navigation.setParams({ taskId: task?.id ?? null });
    };
    if (onBeforeSelect) {
      onBeforeSelect(performSelect);
    } else {
      performSelect();
    }
  }, [navigation, onBeforeSelect]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderItem = useCallback((info: any) => (
    <TaskItem
      testID={`task-item-level-${info.item.level}-${info.item.sortIndex}`}
      task={info.item}
      isSelected={selectedTaskId === info.item.id}
      isFirstOfLevel={info.section.data.indexOf(info.item) === 0}
      isLastOfLevel={info.section.data.indexOf(info.item) === info.section.data.length - 1}
      readonly={isReadOnly || isMutating}
      onSelect={() => handleSelect(info.item)}
      onMoveUp={() => handleMove(info.item, -1)}
      onMoveDown={() => handleMove(info.item, +1)}
      onDelete={() => handleDelete(info.item)}
    />
  ), [selectedTaskId, isReadOnly, isMutating, handleSelect, handleMove, handleDelete]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderSectionHeader = useCallback((info: any) => (
    <LevelHeader levelNr={info.section.levelNr} title={info.section.title} />
  ), []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderSectionFooter = useCallback((info: any) => (
    <Box testID={`add-task-button-box-${info.section.levelNr}`}>
      <AddTaskButton
        onPress={() => handleAddTask(info.section.levelNr)}
        isReadonly={isReadOnly}
        isDisabled={isMutating}
      />
    </Box>
  ), [handleAddTask, isReadOnly, isMutating]);

  if (!visible) {
    return null;
  }
  if (isLoading || isLoadingProp) {
    return (
      <Box flex={1} justifyContent="center" alignItems="center">
        <Spinner testID="tasks-loading" />
      </Box>
    );
  }

  return (
    <Box flex={1} pt="$2">
      <Box px="$2" mb="$2">
        <Pressable
          onPress={() => handleSelect(null)}
          p="$2"
          px="$3"
          borderRadius="$md"
          bg={isSettingsSelected ? '$bgHover' : 'transparent'}
          opacity={isSettingsSelected ? 1 : 0.8}
          testID="sidebar-item-module-settings"
        >
          <HStack alignItems="center" space="md">
            <Icon
              as={Settings}
              size="sm"
              color={isSettingsSelected ? '$brandBase' : '$textSubtle'}
            />
            <Text
              size="sm"
              fontWeight={isSettingsSelected ? 'semibold' : 'medium'}
              color={isSettingsSelected ? '$textHeading' : '$textBody'}
            >
              {t('lecturer.moduleEditor.moduleEditor.settingsTitle', {
                defaultValue: 'Module Settings',
              })}
            </Text>
          </HStack>
        </Pressable>
      </Box>

      <SectionList
        testID="task-list"
        px={0}
        sections={sections}
        keyExtractor={(item: Task) => item.id}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        renderItem={renderItem as any}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        renderSectionHeader={renderSectionHeader as any}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        renderSectionFooter={renderSectionFooter as any}
        flex={1}
      />
    </Box>
  );
};

export default TaskList;
